import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSchools } from "../../hooks/useSchools";
import {
  validateName,
  validateEmail,
  validateURL,
  sanitizeInput,
  sanitizeInputLight,
  sanitizeTextContent,
  sanitizeTextContentLight,
  sanitizeURL,
  sanitizeSchoolCode,
  sanitizeFormData,
} from "../../lib/sanitizer";

export default function SchoolForm({ onSuccess, onCancel }) {
  const navigate = useNavigate();
  const { createSchool, loading } = useSchools();

  const [formData, setFormData] = useState({
    name: "",
    short_name: "",
    code_university: "",
    address: "",
    description: "",
    level: "university",
    image: "",
    contact: {
      phone: "",
      email: "",
      website: "",
    },
  });

  const [errors, setErrors] = useState({});

  const schoolLevels = [
    { value: "primary", label: "Tiểu học" },
    { value: "secondary", label: "Trung học cơ sở" },
    { value: "high", label: "Trung học phổ thông" },
    { value: "university", label: "Đại học" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Sử dụng sanitize nhẹ cho quá trình gõ, không trim hoặc normalize spaces
    let sanitizedValue = value;
    if (name === "name" || name === "short_name" || name === "address") {
      sanitizedValue = sanitizeInputLight(value);
    } else if (name === "description") {
      sanitizedValue = sanitizeTextContentLight(value);
    } else if (name === "code_university") {
      // Giữ nguyên logic cho mã trường vì nó cần xử lý đặc biệt
      sanitizedValue = sanitizeSchoolCode(value);
    } else if (name === "contact.email" || name === "contact.phone") {
      sanitizedValue = sanitizeInputLight(value);
    } else if (name === "image" || name === "contact.website") {
      // Cho phép gõ tự do, sẽ validate khi submit
      sanitizedValue = value;
    }

    if (name.startsWith("contact.")) {
      const contactField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        contact: {
          ...prev.contact,
          [contactField]: sanitizedValue,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate name with enhanced sanitization
    const nameValidation = validateName(formData.name);
    if (!nameValidation.isValid) {
      newErrors.name = nameValidation.error;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Địa chỉ là bắt buộc";
    }

    if (!formData.level) {
      newErrors.level = "Cấp học là bắt buộc";
    }

    // Validate university code for universities
    if (formData.level === "university" && !formData.code_university.trim()) {
      newErrors.code_university = "Mã trường đại học là bắt buộc";
    }

    // Validate email format if provided
    if (formData.contact.email) {
      const emailValidation = validateEmail(formData.contact.email);
      if (!emailValidation.isValid) {
        newErrors["contact.email"] = emailValidation.error;
      }
    }

    // Validate website format if provided
    if (formData.contact.website) {
      const urlValidation = validateURL(formData.contact.website);
      if (!urlValidation.isValid) {
        newErrors["contact.website"] = urlValidation.error;
      }
    }

    // Validate image URL if provided
    if (formData.image) {
      const imageValidation = validateURL(formData.image);
      if (!imageValidation.isValid) {
        newErrors.image = "URL hình ảnh không hợp lệ";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      // Sanitize all form data before sending
      const sanitizedData = sanitizeFormData(formData);

      // Remove empty contact fields
      const cleanedData = {
        ...sanitizedData,
        contact: Object.fromEntries(
          Object.entries(sanitizedData.contact).filter(
            ([_, value]) => value && value.trim()
          )
        ),
      };

      // Remove empty fields
      Object.keys(cleanedData).forEach((key) => {
        if (typeof cleanedData[key] === "string" && !cleanedData[key].trim()) {
          delete cleanedData[key];
        }
      });

      const result = await createSchool(cleanedData);

      if (result.success !== false) {
        if (onSuccess) {
          onSuccess(result);
        } else {
          navigate("/schools");
        }
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      setErrors({ submit: "Đã xảy ra lỗi không mong muốn" });
    }
  };

  return (
    <div className="schools__form-container">
      <div
        className={`schools__form ${loading ? "schools__form--loading" : ""}`}
      >
        <div className="schools__form-card">
          {/* Enhanced Header with gradient background */}
          <div className="schools__form-header">
            <h1 className="schools__form-title">Thêm trường mới</h1>
            <p className="schools__form-subtitle">
              Thông tin trường sẽ được gửi để admin xem xét và duyệt
            </p>
          </div>

          <div className="schools__form-body">
            <form onSubmit={handleSubmit} className="schools__form-form">
              {/* Basic Information Section */}
              <div className="schools__form-section">
                <h3 className="schools__form-section-title">
                  Thông tin cơ bản
                </h3>

                <div className="schools__form-row">
                  <div className="schools__form-field">
                    <label className="schools__form-label schools__form-label--name schools__form-label--required">
                      Tên trường
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`schools__form-input ${
                        errors.name ? "schools__form-input--error" : ""
                      }`}
                      placeholder="Tên đầy đủ của trường học"
                    />
                    {errors.name && (
                      <span className="schools__form-error">{errors.name}</span>
                    )}
                  </div>

                  <div className="schools__form-field">
                    <label className="schools__form-label schools__form-label--name">
                      Tên viết tắt
                    </label>
                    <input
                      type="text"
                      name="short_name"
                      value={formData.short_name}
                      onChange={handleChange}
                      className="schools__form-input"
                      placeholder="VD: HUST, UET, NEU"
                    />
                  </div>
                </div>

                <div className="schools__form-row">
                  <div className="schools__form-field">
                    <label className="schools__form-label schools__form-label--level schools__form-label--required">
                      Cấp học
                    </label>
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleChange}
                      className={`schools__form-select ${
                        errors.level ? "schools__form-select--error" : ""
                      }`}
                    >
                      {schoolLevels.map((level) => (
                        <option key={level.value} value={level.value}>
                          {level.label}
                        </option>
                      ))}
                    </select>
                    {errors.level && (
                      <span className="schools__form-error">
                        {errors.level}
                      </span>
                    )}
                  </div>

                  {formData.level === "university" && (
                    <div className="schools__form-field">
                      <label className="schools__form-label schools__form-label--code schools__form-label--required">
                        Mã trường đại học
                      </label>
                      <input
                        type="text"
                        name="code_university"
                        value={formData.code_university}
                        onChange={handleChange}
                        className={`schools__form-input ${
                          errors.code_university
                            ? "schools__form-input--error"
                            : ""
                        }`}
                        placeholder="VD: HUST, UET, NEU"
                      />
                      {errors.code_university && (
                        <span className="schools__form-error">
                          {errors.code_university}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="schools__form-field schools__form-field--full-width">
                  <label className="schools__form-label schools__form-label--address schools__form-label--required">
                    Địa chỉ
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`schools__form-input ${
                      errors.address ? "schools__form-input--error" : ""
                    }`}
                    placeholder="Số nhà, tên đường, phường/xã, quận/huyện, tỉnh/thành phố"
                  />
                  {errors.address && (
                    <span className="schools__form-error">
                      {errors.address}
                    </span>
                  )}
                </div>

                <div className="schools__form-field schools__form-field--full-width">
                  <label className="schools__form-label schools__form-label--description">
                    Mô tả về trường
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="schools__form-textarea"
                    placeholder="Mô tả ngắn gọn về trường: đặc điểm nổi bật, lịch sử, thành tựu..."
                    rows="4"
                  />
                </div>

                <div className="schools__form-field schools__form-field--full-width">
                  <label className="schools__form-label schools__form-label--image">
                    Hình ảnh trường (URL)
                  </label>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    className="schools__form-input"
                    placeholder="https://example.com/school-image.jpg"
                  />
                  <small
                    style={{
                      marginTop: "8px",
                      color: "#6B7280",
                      fontSize: "14px",
                    }}
                  >
                    Bạn có thể upload hình ảnh lên dịch vụ như Imgur, Google
                    Drive và dán link vào đây
                  </small>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="schools__form-section">
                <h3 className="schools__form-section-title">
                  Thông tin liên hệ
                </h3>

                <div className="schools__form-contact-grid">
                  <div className="schools__form-field">
                    <label className="schools__form-label schools__form-label--phone">
                      Số điện thoại
                    </label>
                    <input
                      type="tel"
                      name="contact.phone"
                      value={formData.contact.phone}
                      onChange={handleChange}
                      className="schools__form-input"
                      placeholder="024-3869-2008"
                    />
                  </div>

                  <div className="schools__form-field">
                    <label className="schools__form-label schools__form-label--email">
                      Email
                    </label>
                    <input
                      type="email"
                      name="contact.email"
                      value={formData.contact.email}
                      onChange={handleChange}
                      className={`schools__form-input ${
                        errors["contact.email"]
                          ? "schools__form-input--error"
                          : ""
                      }`}
                      placeholder="info@school.edu.vn"
                    />
                    {errors["contact.email"] && (
                      <span className="schools__form-error">
                        {errors["contact.email"]}
                      </span>
                    )}
                  </div>

                  <div className="schools__form-field">
                    <label className="schools__form-label schools__form-label--website">
                      Website
                    </label>
                    <input
                      type="url"
                      name="contact.website"
                      value={formData.contact.website}
                      onChange={handleChange}
                      className={`schools__form-input ${
                        errors["contact.website"]
                          ? "schools__form-input--error"
                          : ""
                      }`}
                      placeholder="https://school.edu.vn"
                    />
                    {errors["contact.website"] && (
                      <span className="schools__form-error">
                        {errors["contact.website"]}
                      </span>
                    )}
                  </div>
                </div>

                <small
                  style={{
                    marginTop: "16px",
                    color: "#6B7280",
                    fontSize: "14px",
                    display: "block",
                  }}
                >
                  💡 Các thông tin liên hệ giúp người dùng dễ dàng tìm hiểu thêm
                  về trường
                </small>
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="schools__form-submit-error">
                  {errors.submit}
                </div>
              )}
            </form>
          </div>

          {/* Action Buttons */}
          <div className="schools__form-actions">
            <button
              type="button"
              onClick={onCancel || (() => navigate("/schools"))}
              className="btn btn--secondary"
              disabled={loading}
            >
              Hủy bỏ
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn--primary"
              onClick={handleSubmit}
            >
              {loading ? "Đang gửi..." : "Gửi để duyệt"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
